import { HomeView } from '@/views/Home/Home.view';
import { withPreRenderSettings } from '@/utils/hocs';
import { getSeoFullData } from '@/api/helpers/seo.helpers';
import {
  GetAdsResponse,
  GetPopularRequestsResponse,
  ServiceName,
} from '@/api/types/ads.types';
import { ServerDataProvider } from '@/contexts';
import { apolloClient } from '@/api/apolloClient';
import { GET_ADS, GET_POPULAR_REQUESTS } from '@/api/queries/ads.queries';
import { AdsChunkSize } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.types';
import { PlatformSource } from '@/api/types/common.types';
import { getPlatformSource } from '@/utils/getPlatformSource';
import { GET_ROOT_CATEGORIES } from '@/api/queries/categories.queries';
import { GetRootCategoriesResponse } from '@/api/types/categories.types';
import { getLoggingHeaders } from '@/api/helpers/ad.helpers';

function Home({
  popularRequests,
  rootCategories,
}: {
  popularRequests: GetPopularRequestsResponse | null;
  rootCategories: GetRootCategoriesResponse | null;
}) {
  return (
    <ServerDataProvider
      popularRequests={popularRequests}
      rootCategories={rootCategories}
    >
      <HomeView />
    </ServerDataProvider>
  );
}

Home.getInitialProps = withPreRenderSettings(
  async (context, client, deviceType) => {
    const isBrowser = typeof window !== 'undefined';

    const seoFullData = await getSeoFullData(client, context);
    const userAgent = context.req?.headers['user-agent'] || '';
    const isSSRWebView = userAgent.indexOf('miniApp') >= 0;
    const platformSource = getPlatformSource(deviceType);

    if (!isBrowser) {
      const source = isSSRWebView ? PlatformSource.App : platformSource;
      const premiumQuery = GET_ADS(ServiceName.Premium);
      const premiumVariables = {
        adKind: ServiceName.Premium,
        first: AdsChunkSize.Middle,
        source,
      };

      const premiumPromise =
        process.env.E2E_TEST_MODE === 'true'
          ? import('@/__e2e__/mock/adsPremium').then(
              async ({ getAdsPremium }) => {
                const { generateRandomCursor } = await import(
                  '@/utils/helpers'
                );
                client.writeQuery({
                  query: premiumQuery,
                  variables: premiumVariables,
                  data: getAdsPremium(generateRandomCursor()),
                });
              }
            )
          : getLoggingHeaders(context, source).then(headers =>
              apolloClient<GetAdsResponse>(
                client,
                {
                  query: premiumQuery,
                  variables: premiumVariables,
                  context: { headers },
                },
                context
              )
            );

      const [, popularRequestsResult, rootCategoriesResult] = await Promise.all(
        [
          premiumPromise,
          apolloClient<GetPopularRequestsResponse>(
            client,
            {
              query: GET_POPULAR_REQUESTS,
              variables: { path: context.pathname },
            },
            context
          ),
          apolloClient<GetRootCategoriesResponse>(
            client,
            { query: GET_ROOT_CATEGORIES },
            context
          ),
        ]
      );

      return {
        popularRequests: popularRequestsResult?.data ?? null,
        rootCategories: rootCategoriesResult?.data ?? null,
        seoFullData: seoFullData ?? null,
      };
    }

    return { seoFullData: seoFullData ?? null };
  }
);

export default Home;
